import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Grid, TextField, Typography, Alert, Paper, Button, Chip, Autocomplete, InputAdornment, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { presets } from '../products/productPresets'
import { shownFieldKeys } from './productFieldsConfig'
import { formatRingSize, parseRingSize, isQuarterSize } from './ringSize'
import { titleAfterSave } from './productTitle'

// CreateProduct's labels where it has the field; the rest are new to this panel.
const LABEL_KEYS = {
    weight: 'products.fields.weight',
    ring_size: 'products.fields.ringSize',
    width: 'photoUpload.fields.width',
    height: 'photoUpload.fields.height',
    length: 'products.fields.length',
    name: 'photoUpload.fields.name',
    metal: 'products.fields.metal',
    color: 'products.fields.color',
    gemstones: 'products.fields.gemstones',
    center_stone: 'products.fields.centerStone',
    side_stones: 'products.fields.sideStones',
    side_stone_count: 'photoUpload.fields.sideStoneCount',
    band_width: 'products.fields.bandWidth',
    watch_model: 'products.fields.modelNumber',
    // Not a field, but a save can write it (a failed save lists it).
    title: 'photoUpload.fields.siteTitle',
}

// CreateProduct's adornments, plus mm for width and height (Daniel, 2026-10-30).
const UNITS = { weight: 'g', width: 'mm', height: 'mm', length: 'in.', band_width: 'mm', center_stone: 'ct', side_stones: 'ct' }

// CreateProduct's gemstone list: the preset keys, title-cased ('cubic_zirconia' -> 'Cubic Zirconia').
const GEMSTONES = Object.keys(presets.gemstones).map(key => key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))

export function fieldLabel(t, key) {
    return t(LABEL_KEYS[key] ?? key)
}

// A field's value as read-only text, for the summary of the fields the
// category does not require.
function displayValue(key, text) {
    if (Array.isArray(text)) return text.length ? text.join(', ') : '—'
    if (String(text ?? '').trim() === '') return '—'
    return UNITS[key] ? `${text} ${UNITS[key]}` : text
}

// The whole size the clerk typed so far ("6", "6.25", "6¼" -> 6), or null.
function wholeRingSize(text) {
    const size = parseRingSize(text)
    if (size !== null) return Math.floor(size)
    const leading = String(text ?? '').match(/^\s*(\d+)/)
    return leading ? Number(leading[1]) : null
}

// Sizes run 1 to 16 in quarters (the server refuses anything else).
const inRingRange = size => size >= 1 && size <= 16

// A phone's decimal keypad has no space, slash or ¼, so the fraction is a tap:
// the clerk types the whole size and picks 6, 6¼, 6½ or 6¾ here. A whole size
// with no valid quarter (0, 67) offers none; 16 offers only 16.
function RingFractions({ text, onPick, label }) {
    const typed = wholeRingSize(text)
    const whole = typed !== null && inRingRange(typed) ? typed : null
    const size = parseRingSize(text)
    const selected = whole !== null && size !== null && isQuarterSize(size) ? size : null
    return (
        <ToggleButtonGroup
            exclusive
            fullWidth
            size='small'
            color='primary'
            value={selected}
            onChange={(_e, value) => { if (value !== null) onPick(formatRingSize(value)) }}
            aria-label={label}
            sx={{ mt: 1 }}
        >
            {[0, 0.25, 0.5, 0.75].map(fraction => {
                const value = whole === null ? fraction : whole + fraction
                return (
                    <ToggleButton key={fraction} value={value} disabled={whole === null || !inRingRange(value)} sx={{ fontSize: '1rem', py: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                        {whole === null ? (['x', 'x¼', 'x½', 'x¾'][fraction * 4]) : formatRingSize(value)}
                    </ToggleButton>
                )
            })}
        </ToggleButtonGroup>
    )
}

// The title on the website once the save lands: a save that touches the name,
// metal, color or stones rebuilds it, with the product screen's rule.
function SiteTitle({ product, draft, baseline }) {
    const { t } = useTranslation()
    const title = titleAfterSave(product, draft, baseline)
    const changes = title !== product.title
    return (
        <Box data-testid='site-title' sx={{ mb: 2, minWidth: 0 }}>
            <Typography variant='caption' color='text.secondary' component='div'>{t('photoUpload.fields.siteTitle')}</Typography>
            <Typography variant='body2' fontWeight={500} sx={{ overflowWrap: 'anywhere' }}>{title}</Typography>
            {changes && (
                <Typography variant='caption' color='text.secondary' component='div' sx={{ overflowWrap: 'anywhere' }}>
                    {t('photoUpload.fields.siteTitleNow', { title: product.title })}
                </Typography>
            )}
        </Box>
    )
}

export default function ProductFields({ form, onRetry }) {
    const { t } = useTranslation()
    const [othersOpen, setOthersOpen] = useState(false)
    const { status, product, draft, baseline, problems, saving, saveResult, hasChanges, loadError } = form

    if (status === 'loading') return <CircularProgress size={20} data-testid='fields-busy' />
    if (status === 'failed') {
        return (
            <Alert severity='warning' action={<Button color='inherit' size='small' onClick={onRetry}>{t('photoUpload.fields.retry')}</Button>}>
                {t('photoUpload.fields.loadFailed', { error: loadError || t('common.unknownError') })}
            </Alert>
        )
    }
    if (status !== 'loaded') return null

    const required = product.required ?? []
    const keys = shownFieldKeys(product.category, required, product.fields)
    const requiredKeys = keys.filter(key => required.includes(key))
    const otherKeys = keys.filter(key => !required.includes(key))
    // A changed field the server would refuse stays on screen: the summary
    // would hide the very input the Upload caption is pointing at.
    const othersNeedFixing = otherKeys.some(key => problems[key])
    const othersShown = othersOpen || othersNeedFixing
    const fieldStatus = Object.keys(problems).length ? 'incomplete' : hasChanges ? 'unsaved' : 'complete'
    const statusColor = { incomplete: 'error', unsaved: 'warning', complete: 'success' }[fieldStatus]
    const listOptions = {
        metal: product.choices?.metal ?? Object.keys(presets.metals),
        color: product.choices?.color ?? presets.colors,
        // A stone stored on the product but not in the presets stays selectable.
        gemstones: [...new Set([...GEMSTONES, ...(draft.gemstones ?? [])])],
    }

    function helperText(key) {
        return problems[key] ? t(`photoUpload.fields.problems.${problems[key]}`) : undefined
    }

    // The required ones are the clerk's job at the table (the scale, the ring
    // sizer), so they get full-size inputs and numerals; the rest stay compact.
    function textProps(key) {
        const isRequired = required.includes(key)
        return {
            fullWidth: true,
            size: isRequired ? 'medium' : 'small',
            label: fieldLabel(t, key),
            required: required.includes(key),
            error: Boolean(problems[key]),
            helperText: helperText(key),
        }
    }

    function renderField(key) {
        if (key === 'metal') {
            return (
                <Autocomplete
                    blurOnSelect
                    id='fields-metal'
                    value={draft.metal || null}
                    options={listOptions.metal}
                    onChange={(_e, value) => form.setField('metal', value ?? '')}
                    renderInput={params => <TextField {...params} {...textProps(key)} />}
                />
            )
        }
        if (key === 'color' || key === 'gemstones') {
            return (
                <Autocomplete
                    multiple
                    filterSelectedOptions
                    id={`fields-${key}`}
                    value={draft[key] ?? []}
                    options={listOptions[key]}
                    onChange={(_e, value) => form.setField(key, value)}
                    renderInput={params => <TextField {...params} {...textProps(key)} />}
                />
            )
        }
        const unit = UNITS[key]
        return (
            <TextField
                {...textProps(key)}
                id={`fields-${key}`}
                autoComplete='off'
                value={draft[key] ?? ''}
                inputProps={{
                    inputMode: key === 'side_stone_count' ? 'numeric' : unit || key === 'ring_size' ? 'decimal' : 'text',
                    ...(required.includes(key) && { style: { fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' } }),
                }}
                InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
                // An empty half-width field would otherwise run its label into the unit.
                InputLabelProps={unit ? { shrink: true } : undefined}
                onChange={e => form.setField(key, e.target.value)}
                // "6.25" or "6 1/4" reads back as "6¼" once the clerk moves on.
                onBlur={key === 'ring_size' ? () => {
                    const size = parseRingSize(draft.ring_size)
                    if (size !== null && isQuarterSize(size)) form.setField('ring_size', formatRingSize(size))
                } : undefined}
            />
        )
    }

    const names = list => list.map(key => fieldLabel(t, key)).join(', ')

    const wide = key => ['color', 'gemstones', 'name'].includes(key)

    return (
        <Paper variant='outlined' sx={{ p: 2, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                <Typography variant='subtitle1' fontWeight={600}>{t('photoUpload.fields.title')}</Typography>
                <Chip
                    size='small'
                    variant='outlined'
                    color={statusColor}
                    data-testid='fields-status'
                    label={t(`photoUpload.fields.status.${fieldStatus}`)}
                />
            </Box>
            <SiteTitle product={product} draft={draft} baseline={baseline} />
            <Grid container spacing={1.5}>
                {requiredKeys.map(key => (
                    <Grid item key={key} xs={wide(key) ? 12 : 6}>
                        {renderField(key)}
                        {key === 'ring_size' && <RingFractions text={draft.ring_size} label={t('photoUpload.fields.ringFractions')} onPick={value => form.setField('ring_size', value)} />}
                    </Grid>
                ))}
            </Grid>
            {otherKeys.length > 0 && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant='body2' color='text.secondary'>{t('photoUpload.fields.others')}</Typography>
                        <Button
                            size='small'
                            onClick={() => setOthersOpen(open => !open)}
                            disabled={othersNeedFixing}
                            aria-expanded={othersShown}
                            aria-label={t(othersShown ? 'photoUpload.fields.hideOthers' : 'photoUpload.fields.editOthers')}
                        >
                            {t(othersShown ? 'common.close' : 'common.edit')}
                        </Button>
                    </Box>
                    {othersShown ? (
                        <Grid container spacing={1.5}>
                            {otherKeys.map(key => (
                                <Grid item key={key} xs={wide(key) ? 12 : 6}>
                                    {renderField(key)}
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box component='dl' sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 1, m: 0 }}>
                            {otherKeys.map(key => (
                                <Box key={key} data-testid={`other-field-${key}`} sx={{ minWidth: 0 }}>
                                    <Typography component='dt' variant='caption' color='text.secondary'>{fieldLabel(t, key)}</Typography>
                                    <Typography component='dd' variant='body2' sx={{ m: 0, overflowWrap: 'anywhere' }}>{displayValue(key, draft[key])}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
            {saveResult && (
                <Alert severity={saveResult.type} data-testid='fields-save-result' sx={{ mt: 1.5 }}>
                    {saveResult.type === 'success' ? t('photoUpload.fields.saved') : (<>
                        {t('photoUpload.fields.saveFailed', { error: saveResult.error })}
                        {saveResult.partial && (<>
                            {saveResult.partial.landed.length > 0 && <div>{t('photoUpload.fields.landed', { fields: names(saveResult.partial.landed) })}</div>}
                            {saveResult.partial.failed.length > 0 && <div>{t('photoUpload.fields.failed', { fields: names(saveResult.partial.failed) })}</div>}
                            {saveResult.partial.notAttempted.length > 0 && <div>{t('photoUpload.fields.notAttempted', { fields: names(saveResult.partial.notAttempted) })}</div>}
                        </>)}
                    </>)}
                </Alert>
            )}
            <Grid container justifyContent='flex-end' sx={{ mt: 1.5 }}>
                <Button
                    variant='outlined'
                    onClick={form.save}
                    disabled={!hasChanges || saving || Object.keys(problems).length > 0}
                >
                    {t('photoUpload.fields.save')}
                </Button>
            </Grid>
        </Paper>
    )
}
